import type { CreateOrderRequest, ReceiptData } from '@matix/types';
import { useOutbox } from '../../store/outbox';

/**
 * Can this basket be taken offline and posted later?
 *
 * Only a plain sale can. Everything refused here is refused for the same
 * reason: the server prices or validates it against state the till does not
 * have, so a queued copy could be rejected at sync — after the money was taken
 * and the receipt was printed. That is strictly worse than telling the cashier
 * now that this particular sale needs the network.
 *
 *   returns / exchanges  the parent invoice and its caps live server-side
 *   loyalty redemption   the balance is server truth; queueing risks
 *                        spending points that are gone by sync time
 *   gift cards           same, and a card can be spent at another till
 *   coupons              validated and priced server-side, per use
 *   membership           creates or spends a prepaid package server-side
 *   delivery             customer, address and driver are validated server-side
 *   receipt edit         a different endpoint against a row that must exist
 *
 * The automatic promotion and membership engines are a different case and are
 * NOT refused: the server skips them for an offline sale (`offline_capture`)
 * so the basket simply does not get them. See docs/OFFLINE-CASHIER-PLAN.md §3.
 *
 * `offlineEnabled` is the tenant's master switch (Admin → POS Settings →
 * Offline Sales). It is a REQUIRED parameter rather than an optional one with a
 * permissive default, so a new call site cannot quietly skip the gate — the
 * compiler asks. The server enforces the same rule independently; this is what
 * stops the till taking money it will not be able to post.
 */
export function offlineBlockReason(
  req: CreateOrderRequest,
  editingReceiptId: number | null,
  offlineEnabled: boolean,
): string | null {
  // First, because it outranks every basket-shape reason: when the tenant has
  // not enabled offline sales there is nothing to evaluate.
  if (!offlineEnabled) return 'DISABLED';
  if (editingReceiptId != null) return 'EDIT';
  if (req.parent_receipt_id != null) return 'RETURN';
  if (req.refund_payments && req.refund_payments.length > 0) return 'RETURN';
  if (req.items.some((l) => l.qty < 0)) return 'RETURN';
  if (req.loyalty?.redeem_points) return 'LOYALTY';
  if (req.gift_cards) return 'GIFT_CARD';
  if (req.coupon?.code || req.coupon_code) return 'COUPON';
  if (req.membership) return 'MEMBERSHIP';
  if (req.order_type === 2 || req.delivery) return 'DELIVERY';
  // A laundry order prints its number + barcode on the bag tag at once.
  if (req.laundry_order) return 'LAUNDRY';
  return null;
}

export function canQueueOffline(
  req: CreateOrderRequest,
  editingReceiptId: number | null,
  offlineEnabled: boolean,
): boolean {
  return offlineBlockReason(req, editingReceiptId, offlineEnabled) === null;
}

/**
 * Turn the request the charge path already built into a queued sale.
 *
 * The request is stored as-is apart from two additions, both of which have to
 * be made HERE rather than at sync time, because they describe the moment the
 * money was taken and that moment is gone by then:
 *
 *   offline_capture  tells the server to skip the automatic promotion and
 *                    membership engines. Without it the server prices a
 *                    discount the cashier never collected and refuses the
 *                    sale with PAYMENT_SUM_MISMATCH.
 *   pos_shift_id     pins the receipt to the shift it was rung in. Without it
 *                    the server attaches it to whatever shift is open at sync,
 *                    which may be the next cashier's.
 */
export async function queueSaleOffline(input: {
  request: CreateOrderRequest;
  receipt: ReceiptData | null;
  /** From `nextProvisionalNo` — the caller needs it first, to print with. */
  provisionalNo: string;
  drawerId: number | null;
  shiftId: number | null;
  summary: { total: number; itemCount: number; customerLabel: string | null };
}): Promise<string> {
  const capturedAt = input.request.entry_date ?? new Date().toISOString();
  const { provisionalNo } = input;

  const request: CreateOrderRequest = {
    ...input.request,
    ...(input.shiftId != null ? { pos_shift_id: input.shiftId } : {}),
    offline_capture: {
      captured_at: capturedAt,
      terminal_ref: input.drawerId != null ? `drawer-${input.drawerId}` : null,
      // The number already printed on the customer's copy. The server puts it
      // on the synced invoice's reference and memo, so the paper in the
      // customer's hand can be matched to the invoice in the ERP.
      provisional_no: provisionalNo,
    },
  };

  await useOutbox.getState().enqueue({
    id: request.idempotency_key,
    capturedAt,
    provisionalNo,
    request,
    receipt: input.receipt,
    summary: input.summary,
    lastError: null,
    result: null,
    syncedAt: null,
  });

  return provisionalNo;
}
